import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { quoteExtraGuestSurcharge, quoteExtraGuestSurchargePatterns } from 'api/reservation'

export interface BnblRoomExtraGuestSurcharge {
  /** LE-charged sell amount for the whole stay. */
  total: number
  /** Portion collected by the hotel directly (not charged by LE). */
  payableAtProperty: number
  applies: boolean
  /** True when neither the patterns call nor the single-occupancy fallback could price this room. */
  unpriceable: boolean
}

const rateKey = (roomTypeId?: string, roomRateId?: string, nights?: number) =>
  `${roomTypeId}-${roomRateId}-${nights}`

const matchesOccupancy = (
  pattern: { adults: number, children: number, infants: number, teenagers: number },
  occupancy: App.Occupants,
) =>
  pattern.adults === occupancy.adults &&
  pattern.children === (occupancy.children ?? 0) &&
  pattern.infants === (occupancy.infants ?? 0) &&
  pattern.teenagers === (occupancy.teenagers ?? 0)

/**
 * Quotes the upfront extra-guest surcharge for every BNBL room by pricing each room rate ONCE
 * (svc-reservation returns every bookable occupancy pattern in a single call) and looking up the
 * pattern for each room's occupancy. If an occupancy isn't in the returned patterns (rare — the
 * picker allows age-classified occupancies the capacity enumeration may miss) it falls back to a
 * single-occupancy quote; a room only ends up `unpriceable` if that fallback also fails. All
 * pricing stays server-side. `perRoom` is in the same flat (package, then occupancy) order the
 * checkout cart is built in.
 */
export function useBnblExtraGuestSurcharges(
  offer: App.Offer,
  bnblModalPackages: Array<App.BnblModal> | undefined,
  currency: string,
  enabled: boolean,
) {
  const propertyId = offer.property?.id

  const rooms = useMemo(
    () =>
      (bnblModalPackages ?? []).flatMap(modalPackage =>
        modalPackage.occupancies.map(occupancy => ({
          pkg: modalPackage.packageView.package,
          occupancy,
        })),
      ),
    [bnblModalPackages],
  )

  // One patterns request per unique room rate (roomType + roomRate + nights), not per occupancy.
  const uniqueRates = useMemo(() => {
    const byKey = new Map<string, { pkg: (typeof rooms)[number]['pkg'], key: string }>()
    for (const { pkg } of rooms) {
      const key = rateKey(pkg.roomType?.id, pkg.roomRate?.id, pkg.duration)
      if (!byKey.has(key)) {
        byKey.set(key, { pkg, key })
      }
    }
    return [...byKey.values()]
  }, [rooms])

  const patternsResults = useQueries({
    queries: uniqueRates.map(({ pkg }) => {
      const roomTypeId = pkg.roomType?.id
      const roomRateId = pkg.roomRate?.id
      const nights = pkg.duration
      return {
        queryKey: ['extra-guest-surcharge-patterns', propertyId, roomTypeId, roomRateId, currency, nights, offer.type],
        queryFn: () =>
          quoteExtraGuestSurchargePatterns(propertyId!, roomTypeId, roomRateId, {
            currency,
            nights,
            offerType: offer.type,
          }),
        enabled: enabled && !!propertyId && !!roomTypeId && !!roomRateId && nights > 0,
        // Matches svc-reservation's in-memory FX cache TTL so the quote refreshes in step with FX.
        staleTime: 5 * 60 * 1000,
      }
    }),
  })

  const patternResultByKey = useMemo(
    () => new Map(uniqueRates.map((rate, index) => [rate.key, patternsResults[index]])),
    [uniqueRates, patternsResults],
  )

  // A room needs a single-occupancy fallback quote when the patterns call can't price its occupancy:
  // either the occupancy is missing from a loaded pattern set, OR the patterns quote itself errored
  // (an errored quote prices nothing, so it must fall back too — otherwise the room would silently
  // resolve to a zero surcharge and svc-order would reject the purchase for a mismatched amount).
  const missedRooms = useMemo(
    () =>
      rooms
        .map((room, index) => ({ room, index }))
        .filter(({ room }) => {
          const result = patternResultByKey.get(
            rateKey(room.pkg.roomType?.id, room.pkg.roomRate?.id, room.pkg.duration),
          )
          // Still in flight — wait for it to settle before deciding a fallback is needed.
          if (!result || (result.isLoading && result.fetchStatus !== 'idle')) {
            return false
          }
          if (result.isError) {
            return true
          }
          const patterns = result.data?.patterns
          return patterns != null && !patterns.some(pattern => matchesOccupancy(pattern, room.occupancy))
        }),
    [rooms, patternResultByKey],
  )

  const fallbackResults = useQueries({
    queries: missedRooms.map(({ room }) => {
      const { pkg, occupancy } = room
      const roomTypeId = pkg.roomType?.id
      const roomRateId = pkg.roomRate?.id
      const nights = pkg.duration
      return {
        queryKey: [
          'extra-guest-surcharge-fallback',
          propertyId,
          roomTypeId,
          roomRateId,
          currency,
          nights,
          occupancy.adults,
          occupancy.children,
          occupancy.infants,
          occupancy.teenagers,
          offer.type,
        ],
        queryFn: () =>
          quoteExtraGuestSurcharge(propertyId!, roomTypeId, roomRateId, {
            currency,
            nights,
            numberOfAdults: occupancy.adults,
            numberOfChildren: occupancy.children,
            numberOfInfants: occupancy.infants,
            numberOfTeenagers: occupancy.teenagers,
            offerType: offer.type,
          }),
        enabled: enabled && !!propertyId && !!roomTypeId && !!roomRateId && nights > 0 && occupancy.adults > 0,
        staleTime: 5 * 60 * 1000,
      }
    }),
  })

  return useMemo(() => {
    const fallbackByRoomIndex = new Map(
      missedRooms.map(({ index }, i) => [index, fallbackResults[i]]),
    )

    const perRoom: Array<BnblRoomExtraGuestSurcharge> = rooms.map(({ pkg, occupancy }, index) => {
      const patterns = patternResultByKey.get(rateKey(pkg.roomType?.id, pkg.roomRate?.id, pkg.duration))?.data?.patterns
      const match = patterns?.find(pattern => matchesOccupancy(pattern, occupancy))
      if (match) {
        return {
          total: match.duration_extra_guest_surcharge_total,
          payableAtProperty: match.duration_extra_guest_surcharge_payable_at_property_total,
          applies: match.applies,
          unpriceable: false,
        }
      }

      const fallback = fallbackByRoomIndex.get(index)
      if (fallback?.data) {
        return {
          total: fallback.data.duration_extra_guest_surcharge_total,
          payableAtProperty: fallback.data.duration_extra_guest_surcharge_payable_at_property_total,
          applies: fallback.data.applies,
          unpriceable: false,
        }
      }

      // Missed the patterns AND the fallback quote errored → we can't price this room.
      return { total: 0, payableAtProperty: 0, applies: false, unpriceable: !!fallback?.isError }
    })

    return {
      perRoom,
      total: perRoom.reduce((sum, room) => sum + (room.applies ? room.total : 0), 0),
      // Portion collected by the hotel at check-in (not charged by LE). Surfaced so the offer-page
      // total can include it the same way checkout does, avoiding a modal→checkout surprise.
      payableAtProperty: perRoom.reduce((sum, room) => sum + (room.applies ? room.payableAtProperty : 0), 0),
      // Any room that neither call could price — the caller should route the customer to support.
      unpriceable: perRoom.some(room => room.unpriceable),
      // True once both the patterns and any fallback queries have settled. A DISABLED query reports
      // isLoading=true in react-query v4, so treat idle (never-fetching) queries as settled —
      // otherwise a room we can't quote (missing property/room-rate) would block the CTA forever.
      isReady:
        enabled &&
        patternsResults.every(result => !(result.isLoading && result.fetchStatus !== 'idle')) &&
        fallbackResults.every(result => !(result.isLoading && result.fetchStatus !== 'idle')),
    }
  }, [rooms, patternResultByKey, missedRooms, patternsResults, fallbackResults, enabled])
}
